'use client'

import { useState } from 'react'
import { z } from 'zod'
import { Button } from '@/core/components/form/button/button'
import { ModalTitle } from '@/features/veluvit/components/overlay/layer-parts'
import { segmentSchema } from '@/features/veluvit/schemas/api.schema'
import { api, requestId, runOperation } from '@/features/veluvit/services/api'
import { closeTop, openModal } from '@/features/veluvit/stores/overlay.store'
import {
  roomFor,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'
import { useChatStore } from '@/features/veluvit/stores/chat.store'
import type { Room, RoomResult } from '@/features/veluvit/types/api.types'

const candidateSchema = z.looseObject({
  id: z.string(),
  segments: z.array(segmentSchema),
})
type Candidate = z.infer<typeof candidateSchema>

export function offerReplyCandidate(workId: string, value: unknown): boolean {
  const candidate = candidateSchema.safeParse(value)
  const room = roomFor(workId)
  if (!candidate.success || !room) return false
  openModal(
    <ReplyCandidateModal
      workId={workId}
      roomId={room.id}
      candidate={candidate.data}
    />,
    { label: '새 답변 후보' }
  )
  return true
}

/** 후보 생성과 적용은 별도다. 적용 전 현재 대화를 임의로 덮어쓰지 않는다. */
function ReplyCandidateModal({
  workId,
  roomId,
  candidate,
}: {
  workId: string
  roomId: string
  candidate: Candidate
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  async function select() {
    if (
      pending ||
      useChatStore.getState().get(workId).busy ||
      roomFor(workId)?.id !== roomId
    )
      return
    const identity = useSessionStore.getState().identity
    setPending(true)
    useChatStore.getState().patch(workId, { busy: true })
    try {
      const result = await runOperation<RoomResult>(
        `/room/${encodeURIComponent(roomId)}/select-reply/`,
        { requestId: requestId(), candidateId: candidate.id }
      )
      const room =
        result.room || (await api<Room>(`/room/${encodeURIComponent(roomId)}/`))
      if (
        identity !== useSessionStore.getState().identity ||
        roomFor(workId)?.id !== roomId
      )
        return
      useSessionStore.getState().applyResult({ ...result, room })
      closeTop()
    } catch (failure) {
      if (
        identity === useSessionStore.getState().identity &&
        roomFor(workId)?.id === roomId
      )
        setError(
          failure instanceof Error
            ? failure.message
            : '후보를 적용하지 못했어요.'
        )
    } finally {
      if (
        identity === useSessionStore.getState().identity &&
        roomFor(workId)?.id === roomId
      )
        useChatStore.getState().patch(workId, { busy: false })
      setPending(false)
    }
  }
  return (
    <>
      <ModalTitle>새 답변 후보</ModalTitle>
      <p className="mt-2 text-c13 text-t3">
        후보를 선택하면 서버가 대화와 상태를 갱신해요. 선택 자체에는 생성 비용이
        추가되지 않아요.
      </p>
      <div className="my-4 max-h-[45dvh] space-y-3 overflow-y-auto whitespace-pre-wrap break-words text-c14 leading-relaxed">
        {candidate.segments.map((segment, position) => (
          <p
            key={`${candidate.id}:${segment.id || `${segment.type}:${position}`}`}
          >
            {segment.type === 'speech' && (
              <span className="mb-1 block text-c12 text-t3">
                {segment.speakerType === 'player'
                  ? '나'
                  : segment.speaker || '화자 정보 없음'}
              </span>
            )}
            {segment.text}
          </p>
        ))}
      </div>
      {error && (
        <p role="alert" className="mb-3 text-c13 text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" disabled={pending} onClick={closeTop}>
          현재 답변 유지
        </Button>
        <Button
          variant="accent"
          loading={pending}
          disabled={pending}
          onClick={() => void select()}
        >
          후보 적용
        </Button>
      </div>
    </>
  )
}
